export type ResourceValidationFieldErrors = Record<string, string>;

type TitleRules = {
	minLength: number;
	maxLength: number;
};

type DescriptionRules = {
	minLength: number;
	maxLength: number;
};

type AuthorsRules = {
	min: number;
};

export type ResourceValidationRules = {
	title: TitleRules;
	description: DescriptionRules;
	authors: AuthorsRules;
};

export type ResourceAuthorValidationInput = {
	name?: string | null;
};

export type ResourceFormValidationInput = {
	title: string;
	description: string;
	category: unknown;
	authors: ResourceAuthorValidationInput[];
	publishedDate: string;
	language: string;
	externalUrl: string;
};

const makeFieldError = (
	field: string,
	message: string
): ResourceValidationFieldErrors => {
	return {
		[field]: message
	};
};

export const isValidHttpUrl = (value: string): boolean => {
	try {
		const parsed = new URL(value);
		return (
			parsed.protocol === 'http:' ||
			parsed.protocol === 'https:'
		);
	} catch {
		return false;
	}
};

export const validateTitle = (
	value: string,
	rules: TitleRules
): string | undefined => {
	const title = value.trim();

	if (!title) {
		return 'Title is required';
	}

	if (title.length < rules.minLength) {
		return `Title must be at least ${rules.minLength} characters`;
	}

	if (title.length > rules.maxLength) {
		return `Title cannot exceed ${rules.maxLength} characters`;
	}

	return undefined;
};

export const validateDescription = (
	value: string,
	rules: DescriptionRules
): string | undefined => {
	const description = value.trim();

	if (!description) {
		return 'Description is required';
	}

	if (description.length < rules.minLength) {
		return `Description must be at least ${rules.minLength} characters`;
	}

	if (description.length > rules.maxLength) {
		return `Description cannot exceed ${rules.maxLength} characters`;
	}

	return undefined;
};

export const validateCategory = (
	value: unknown
): string | undefined => {
	if (!value) {
		return 'Category is required';
	}

	return undefined;
};

export const validateAuthors = (
	authors: ResourceAuthorValidationInput[],
	rules: AuthorsRules
): ResourceValidationFieldErrors | undefined => {
	if (authors.length < rules.min) {
		return makeFieldError(
			'authors',
			'At least one author is required'
		);
	}

	const hasAuthorWithName = authors.some(
		(author) => author.name?.trim()
	);

	if (!hasAuthorWithName) {
		const nextErrors: ResourceValidationFieldErrors = {
			authors:
				'At least one author with a name is required'
		};

		authors.forEach((_, index) => {
			nextErrors[`author-name-${index}`] =
				'Author name is required';
		});

		return nextErrors;
	}

	return undefined;
};

export const validatePublishedDate = (
	value: string
): string | undefined => {
	if (!value) {
		return 'Published date is required';
	}

	const parsed = new Date(value);

	if (Number.isNaN(parsed.getTime())) {
		return 'Published date is invalid';
	}

	return undefined;
};

export const validateLanguage = (
	value: string
): string | undefined => {
	if (!value.trim()) {
		return 'Language cannot be empty';
	}

	return undefined;
};

export const validateExternalUrl = (
	value: string
): string | undefined => {
	const externalUrl = value.trim();

	if (!externalUrl) {
		return undefined;
	}

	if (!isValidHttpUrl(externalUrl)) {
		return 'External URL must be a valid http(s) URL';
	}

	return undefined;
};

export const validateResourceForm = (
	form: ResourceFormValidationInput,
	rules: ResourceValidationRules
): ResourceValidationFieldErrors => {
	const titleError = validateTitle(
		form.title,
		rules.title
	);
	if (titleError) {
		return makeFieldError('title', titleError);
	}

	const descriptionError = validateDescription(
		form.description,
		rules.description
	);
	if (descriptionError) {
		return makeFieldError(
			'description',
			descriptionError
		);
	}

	const categoryError = validateCategory(
		form.category
	);
	if (categoryError) {
		return makeFieldError('category', categoryError);
	}

	const authorsError = validateAuthors(
		form.authors,
		rules.authors
	);
	if (authorsError) {
		return authorsError;
	}

	const publishedDateError = validatePublishedDate(
		form.publishedDate
	);
	if (publishedDateError) {
		return makeFieldError(
			'publishedDate',
			publishedDateError
		);
	}

	const languageError = validateLanguage(
		form.language
	);
	if (languageError) {
		return makeFieldError(
			'language',
			languageError
		);
	}

	const externalUrlError = validateExternalUrl(
		form.externalUrl
	);
	if (externalUrlError) {
		return makeFieldError(
			'externalUrl',
			externalUrlError
		);
	}

	return {};
};
